import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ContentSection, FormSectionGroup, PageWrap, SectionHeading } from '@vwfs-bronson/bronson-react';
import { t } from 'i18next';
import ShoppingCart from '../../../../components/ShoppingCart/ShoppingCart';
import { getBrandName } from '../../../../config';
import { CustomerDetailsSection } from '../../../../resources/base/models/enums';
import { isEnv } from '../../../../services/common/miscUtils';
import {
  creditApplicationSelectors,
  setFormAsSubmitted,
} from '../../../../services/redux/features/creditApplication.slice';
import { customSelectors } from '../../../../services/redux/features/custom.slice';
import { CustomerDetailsSubmitSection } from '../CustomerDetailsSubmitSection';
import { SaveAndContinueModal } from '../saveAndContinueModal';
import { createInitialCreditApplication, CustomerDetailsFormGroupType, getFormNamesToRender } from '../utils';
import { AdditionalInfoDataForm } from './AdditionalInfoDataForm';
import { AddressDataForm } from './AddressDataForm';
import { CompanyDataForm } from './CompanyDataForm';
import { ExecutiveInfoDataForm } from './ExecutiveInfoDataForm';
import { GeneralInfoDataForm } from './GeneralInfoDataForm';
import { PaymentDataForm } from './PaymentDataForm';
import { PersonalInfoDataForm } from './PersonalInfoDataForm';
import { SignatoryInfoDataForm } from './SignatoryInfoDataForm';

const getFormsToRender = (isSoleTrader: boolean) => {
  return getFormNamesToRender({ customerType: 'LEGAL_ENTITY', isSoleTrader }).map((formName) => {
    const formGroup: CustomerDetailsFormGroupType = isSoleTrader ? 'soleTrader' : 'business';
    switch (formName) {
      case CustomerDetailsSection.GENERAL_INFO:
        return <GeneralInfoDataForm key={formName} formGroup={formGroup} formName={formName} />;
      case CustomerDetailsSection.ADDRESS_BUSINESS_INFO:
        return <AddressDataForm key={formName} formGroup={formGroup} formName={formName} />;
      case CustomerDetailsSection.PERSONAL_INFO:
        return <PersonalInfoDataForm key={formName} formGroup={formGroup} formName={formName} />;
      case CustomerDetailsSection.ADDITIONAL_INFO:
        return <AdditionalInfoDataForm key={formName} formGroup={formGroup} formName={formName} />;
      case CustomerDetailsSection.COMPANY_INFO:
        if (!isSoleTrader) {
          return null;
        }
        return <CompanyDataForm key={formName} formGroup={formGroup} formName={formName} />;
      case CustomerDetailsSection.EXECUTIVE_INFO:
        return <ExecutiveInfoDataForm key={formName} formGroup={formGroup} formName={formName} />;
      case CustomerDetailsSection.SIGNATORY_INFO:
        return <SignatoryInfoDataForm key={formName} formGroup={formGroup} formName={formName} />;
      case CustomerDetailsSection.PAYMENT_INFO:
        return <PaymentDataForm key={formName} formGroup={formGroup} formName={formName} />;
      default:
        return null;
    }
  });
};

export const BusinessCustomerDetailsForms = () => {
  const isSoleTrader = useSelector(creditApplicationSelectors.selectIsSoleTraderFromCreditApplication);
  const isHybridJourney = useSelector(customSelectors.selectIsHybridJourney);
  const dispatch = useDispatch();
  const { creditApplicationId } = useSelector(creditApplicationSelectors.selectCreditApplicationIds);

  useEffect(() => {
    if (!creditApplicationId) {
      createInitialCreditApplication();
    }
  }, [creditApplicationId]);

  useEffect(() => {
    if (!isSoleTrader) {
      dispatch(setFormAsSubmitted(CustomerDetailsSection.COMPANY_INFO));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSoleTrader]);

  return (
    <PageWrap>
      <ShoppingCart />
      {!isEnv('prod') && <SaveAndContinueModal isHybridJourney={isHybridJourney} />}
      <ContentSection altBackground={getBrandName().includes('vw')} className="u-pv-none">
        <SectionHeading className="u-mt" level={2}>
          {t('step4:heading:businessCustomer')}
        </SectionHeading>
        <FormSectionGroup>{getFormsToRender(isSoleTrader)}</FormSectionGroup>
      </ContentSection>
      <CustomerDetailsSubmitSection customerType="LEGAL_ENTITY" isSoleTrader={isSoleTrader} />
    </PageWrap>
  );
};
